/**
 * F2 — PROJECTION: projectable library + PRESENTER VIEW + live output.
 *
 * - Library: songs / verses / themes / announcements (`projection_item`),
 *   with search and manual add; F1 setlists are IMPORTED materializing the
 *   lyrics within the `projection` scope (see from-setlist.ts).
 * - Presenter: slides per stanza, direct jump, private notes.
 * - Output: the projector is ANOTHER device over P2P-LAN (code AGPJ1,
 *   blind relay + direct WebRTC) or a second window (BroadcastChannel).
 *
 * Isolation remains the 🎯 one: items arrive via the
 * `projection_by_church`/`projection_by_venue` buckets; a director with a
 * grant for ONE venue has only here (and in its SQLite) that venue's items.
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import {
  buildLiveSlide,
  filterProjectionItems,
  getChurchesQuery,
  getProjectionItemsQuery,
  getSetlistSongsQuery,
  getSetlistsQuery,
  getSongsQuery,
  getVenuesQuery,
  goToSlide,
  nextSlide,
  type PresenterState,
  previousSlide,
  PROJECTION_LIVE_VERSION,
  type ProjectionLiveMessage,
  queryKeys,
  setlistToProjectionDrafts,
  sortProjectionItems,
  splitSlides,
} from "@agape/shared";
import {
  type ChurchRecord,
  projection_item,
  type ProjectionItemRecord,
  type SetlistRecord,
  type SetlistSongRecord,
  type SongRecord,
  type VenueRecord,
} from "@agape/powersync";
import {
  Cast,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  ListPlus,
  MonitorPlay,
  Plus,
  StickyNote,
  X,
} from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { TextField } from "@fleet/ui/text-field";
import { showToast } from "@/components/ui/toast";
import {
  createLocalOutput,
  type LiveOutput,
  type PresenterLiveSession,
  startPresenterLiveSession,
} from "@/features/projection/live";
import { Field, FormDialog, TextArea } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

const PROJECTABLE_KINDS = ["song", "verse", "theme", "announcement"] as const;

type LiveStatus = "idle" | "waiting" | "connected" | "local" | "ended";

export function ProjectionPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const query = useDomainQuery<ProjectionItemRecord>(
    (uid) => queryKeys.domain.projectionItems(uid),
    (db) => getProjectionItemsQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const setlists = useDomainQuery<SetlistRecord>(
    (uid) => queryKeys.domain.setlists(uid),
    (db) => getSetlistsQuery(db),
  );
  const setlistSongs = useDomainQuery<SetlistSongRecord>(
    (uid) => queryKeys.domain.setlistSongs(uid),
    (db) => getSetlistSongsQuery(db),
  );
  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );

  const items = createMemo(() => sortProjectionItems(query.data ?? []));
  const [search, setSearch] = createSignal("");
  const visibleItems = createMemo(() => filterProjectionItems(items(), search()));
  const slidesByItem = createMemo(() => items().map((item) => splitSlides(item)));
  const counts = () => slidesByItem().map((s) => s.length);

  const kindLabel = (kind: string | null) => {
    switch (kind) {
      case "song": {
        return t(msg`Song`);
      }
      case "verse": {
        return t(msg`Bible verse`);
      }
      case "theme": {
        return t(msg`Theme / message`);
      }
      case "announcement": {
        return t(msg`Announcement`);
      }
      default: {
        return t(msg`Other`);
      }
    }
  };

  // ── Where I can WRITE projection (nodes with projection:write) ─────────────
  const writeTargets = createMemo(() => {
    const venueNodes = new Set(grants.nodesWith("projection", "write", "venue"));
    const churchNodes = new Set(grants.nodesWith("projection", "write", "church"));
    const options: { value: string; label: string }[] = [];
    for (const church of churches.data ?? []) {
      if (churchNodes.has(church.id)) {
        options.push({
          value: `church:${church.id}`,
          label: church.name ?? church.id,
        });
      }
    }
    for (const venue of venues.data ?? []) {
      if (venueNodes.has(venue.id)) {
        options.push({
          value: `venue:${venue.id}`,
          label: venue.name ?? venue.id,
        });
      }
    }
    return options;
  });
  const canWrite = () => writeTargets().length > 0;

  /** target `church:<id>` / `venue:<id>` → the row's columns. */
  const targetScope = (target: string): { church_id: string; venue_id: string | null } | null => {
    const [kind, id] = target.split(":");
    if (kind === "church" && id) {
      return { church_id: id, venue_id: null };
    }
    if (kind === "venue" && id) {
      const venue = (venues.data ?? []).find((v) => v.id === id);
      return venue?.church_id ? { church_id: venue.church_id, venue_id: id } : null;
    }
    return null;
  };

  // ── Manual add (song/verse/theme/announcement) ─────────────────────────────
  const [addOpen, setAddOpen] = createSignal(false);
  const [addKind, setAddKind] = createSignal<string>("verse");
  const [addTarget, setAddTarget] = createSignal("");
  const [addTitle, setAddTitle] = createSignal("");
  const [addContent, setAddContent] = createSignal("");
  const [addNotes, setAddNotes] = createSignal("");
  const openAdd = () => {
    setAddKind("verse");
    setAddTarget(writeTargets()[0]?.value ?? "");
    setAddTitle("");
    setAddContent("");
    setAddNotes("");
    setAddOpen(true);
  };
  const addItem = async () => {
    const scope = targetScope(addTarget());
    if (!scope || !addTitle().trim()) {
      return;
    }
    await write()
      .insert(projection_item)
      .values({
        id: newId(),
        church_id: scope.church_id,
        venue_id: scope.venue_id,
        kind: addKind(),
        title: addTitle().trim(),
        content: addContent().trim() || null,
        notes: addNotes().trim() || null,
        sort_order: items().length + 1,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setAddOpen(false);
    showToast(t(msg`Added to projection`), "success");
  };

  // ── Import setlist (F1 → projection) ───────────────────────────────────────
  const [importOpen, setImportOpen] = createSignal(false);
  const [importSetlist, setImportSetlist] = createSignal("");
  const [importTarget, setImportTarget] = createSignal("");
  const openImport = () => {
    setImportSetlist("");
    setImportTarget(writeTargets()[0]?.value ?? "");
    setImportOpen(true);
  };
  const runImport = async () => {
    const scope = targetScope(importTarget());
    const setlistId = importSetlist();
    if (!scope || !setlistId) {
      return;
    }
    const lines = (setlistSongs.data ?? []).filter((row) => row.setlist_id === setlistId);
    const drafts = setlistToProjectionDrafts(lines, songs.data ?? [], items().length);
    if (drafts.length === 0) {
      showToast(t(msg`This setlist has no projectable songs synced here.`), "error");
      return;
    }
    for (const draft of drafts) {
      await write().insert(projection_item).values({
        id: newId(),
        church_id: scope.church_id,
        venue_id: scope.venue_id,
        kind: draft.kind,
        title: draft.title,
        content: draft.content,
        notes: draft.notes,
        sort_order: draft.sortOrder,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    }
    setImportOpen(false);
    const count = drafts.length;
    showToast(t(msg`${count} songs added to projection`), "success");
  };

  // ── Presenter ──────────────────────────────────────────────────────────────
  const [presenter, setPresenter] = createSignal<PresenterState | null>(null);
  const openAt = (id: string) => {
    const index = items().findIndex((item) => item.id === id);
    if (index !== -1) {
      setPresenter({ itemIndex: index, slideIndex: 0 });
    }
  };
  const currentItem = () => {
    const state = presenter();
    return state ? items()[state.itemIndex] : undefined;
  };
  const currentSlide = () => {
    const state = presenter();
    if (!state) {
      return null;
    }
    return slidesByItem()[state.itemIndex]?.[state.slideIndex] ?? null;
  };

  // ── Live output (P2P-LAN / second window) ──────────────────────────────────
  const [liveStatus, setLiveStatus] = createSignal<LiveStatus>("idle");
  const [liveCode, setLiveCode] = createSignal<string | null>(null);
  const [liveOutput, setLiveOutput] = createSignal<LiveOutput | null>(null);
  const [codeCopied, setCodeCopied] = createSignal(false);
  let liveSession: PresenterLiveSession | undefined;

  const pushCurrentSlide = () => {
    const output = liveOutput();
    const state = presenter();
    if (!output) {
      return;
    }
    if (!state) {
      output.send({ v: PROJECTION_LIVE_VERSION, type: "blank" });
      return;
    }
    const slide = buildLiveSlide(items(), state);
    const message: ProjectionLiveMessage = slide
      ? { v: PROJECTION_LIVE_VERSION, type: "slide", slide }
      : { v: PROJECTION_LIVE_VERSION, type: "blank" };
    output.send(message);
  };

  // Every slide change (or connected output change) is PUSHED live.
  createEffect(() => {
    void presenter();
    void liveOutput();
    pushCurrentSlide();
  });

  const stopLive = () => {
    liveOutput()?.close();
    liveSession?.close();
    liveSession = undefined;
    setLiveOutput(null);
    setLiveCode(null);
    setLiveStatus("idle");
  };

  const startP2pLive = async () => {
    stopLive();
    setLiveStatus("waiting");
    try {
      const session = await startPresenterLiveSession({
        onClosed: () => setLiveStatus("ended"),
        onHello: () => pushCurrentSlide(),
      });
      liveSession = session;
      setLiveCode(session.code);
      void (async () => {
        try {
          const output = await session.outputPromise;
          setLiveOutput(() => output);
          setLiveStatus("connected");
        } catch {
          setLiveStatus("idle");
          setLiveCode(null);
          showToast(t(msg`The projector did not connect in time.`), "error");
        }
      })();
    } catch {
      setLiveStatus("idle");
      showToast(t(msg`Could not start the projection session.`), "error");
    }
  };

  const startLocalWindow = () => {
    stopLive();
    const output = createLocalOutput(() => pushCurrentSlide());
    setLiveOutput(() => output);
    setLiveStatus("local");
    globalThis.open("/projector?local=1", "_blank", "noopener");
  };

  const copyCode = async () => {
    const code = liveCode();
    if (!code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1500);
    } catch {
      // clipboard not available: the code stays visible on screen
    }
  };

  const closePresenter = () => {
    stopLive();
    setPresenter(null);
  };
  onCleanup(stopLive);

  const liveStatusLabel = () => {
    switch (liveStatus()) {
      case "waiting": {
        return t(msg`Waiting for the projector…`);
      }
      case "connected": {
        return t(msg`Projector connected`);
      }
      case "local": {
        return t(msg`Projecting to a local window`);
      }
      case "ended": {
        return t(msg`Projector disconnected`);
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`Projection`)} />
      <p class="text-sm text-muted-foreground">
        {t(msg`Projectable items your account is allowed to see.`)}
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <div class="min-w-40 flex-1">
          <TextField
            value={search()}
            placeholder={t(msg`Search songs, verses, themes…`)}
            data-testid="projection-search"
            aria-label={t(msg`Search projectable items`)}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <Show when={canWrite()}>
          <Button size="sm" variant="secondary" data-testid="projection-add" onClick={openAdd}>
            <Plus size={14} /> {t(msg`Add item`)}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid="projection-import-setlist"
            onClick={openImport}
          >
            <ListPlus size={14} /> {t(msg`Import setlist`)}
          </Button>
        </Show>
      </div>

      <Show
        when={visibleItems().length > 0}
        fallback={
          <p data-testid="projection-empty" class="text-muted-foreground">
            {items().length === 0
              ? t(msg`Nothing to project here yet.`)
              : t(msg`No items match your search.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="projection-list">
          <For each={visibleItems()}>
            {(item) => (
              <Card data-testid="projection-item" data-projection-id={item.id}>
                <CardContent class="flex items-center justify-between gap-3 py-3">
                  <div class="min-w-0">
                    <p class="truncate font-medium">{item.title}</p>
                    <p class="truncate text-sm text-muted-foreground">
                      {kindLabel(item.kind)}
                      <Show when={item.notes}> · {item.notes}</Show>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid="projection-present"
                    onClick={() => openAt(item.id)}
                    aria-label={t(msg`Present`)}
                  >
                    <MonitorPlay size={16} />
                  </Button>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>

      {/* ── VISTA PRESENTADOR ──────────────────────────────────────────────── */}
      <Show when={presenter()}>
        {(state) => (
          <div
            class="fixed inset-0 z-50 flex flex-col bg-background p-4 sm:p-8"
            data-testid="projection-display"
            role="dialog"
            aria-label={t(msg`Projection view`)}
          >
            {/* Barra superior: salida en vivo + cierre */}
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="projection-live-start"
                  onClick={() => void startP2pLive()}
                >
                  <Cast size={14} /> {t(msg`Project to device`)}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="projection-local-window"
                  onClick={startLocalWindow}
                >
                  <ExternalLink size={14} /> {t(msg`Projector window`)}
                </Button>
                <Show when={liveStatusLabel()}>
                  <span
                    class="rounded-full bg-accent/15 px-2.5 py-1 text-xs text-muted-foreground"
                    data-testid="projection-live-status"
                    data-live-status={liveStatus()}
                  >
                    {liveStatusLabel()}
                  </span>
                </Show>
              </div>
              <Button
                variant="ghost"
                data-testid="projection-close"
                aria-label={t(msg`Close projection`)}
                onClick={closePresenter}
              >
                <X size={18} />
              </Button>
            </div>

            {/* Código de emparejamiento mientras espera al proyector */}
            <Show when={liveCode() && liveStatus() === "waiting"}>
              <div class="mx-auto mt-3 flex w-full max-w-xl flex-col items-center gap-2 rounded-lg border border-border p-4">
                <p class="text-sm text-muted-foreground">
                  {t(msg`On the projector device, open /projector and enter this code:`)}
                </p>
                <p
                  class="select-all break-all text-center font-mono text-sm font-semibold"
                  data-testid="projection-live-code"
                >
                  {liveCode()}
                </p>
                <Button size="sm" variant="ghost" onClick={() => void copyCode()}>
                  <Copy size={14} /> {codeCopied() ? t(msg`Copied`) : t(msg`Copy code`)}
                </Button>
              </div>
            </Show>

            {/* Diapositiva actual */}
            <div class="flex min-h-0 flex-1 flex-col items-center justify-center gap-6">
              <p
                class="max-w-4xl whitespace-pre-line text-center text-3xl font-semibold sm:text-5xl"
                data-testid="projection-slide-body"
              >
                {currentSlide()}
              </p>
              <p class="text-sm text-muted-foreground">
                {currentItem()?.title} · {state().slideIndex + 1}/{counts()[state().itemIndex] ?? 1}
              </p>
              {/* Notas del presentador (solo aquí; el proyector NUNCA las ve) */}
              <Show when={currentItem()?.notes}>
                <p
                  class="flex max-w-2xl items-center gap-2 rounded-md bg-accent/10 px-3 py-2 text-center text-sm text-muted-foreground"
                  data-testid="projection-notes"
                >
                  <StickyNote size={14} class="shrink-0" /> {currentItem()?.notes}
                </p>
              </Show>
            </div>

            {/* Controles + salto directo */}
            <div class="flex flex-col items-center gap-3">
              <div class="flex items-center gap-2">
                <Button
                  variant="secondary"
                  data-testid="projection-prev"
                  aria-label={t(msg`Previous slide`)}
                  onClick={() => setPresenter(previousSlide(state(), counts()))}
                >
                  <ChevronLeft size={18} />
                </Button>
                <Button
                  variant="secondary"
                  data-testid="projection-next"
                  aria-label={t(msg`Next slide`)}
                  onClick={() => setPresenter(nextSlide(state(), counts()))}
                >
                  <ChevronRight size={18} />
                </Button>
              </div>
              <div class="flex max-w-full flex-wrap items-center justify-center gap-1.5 overflow-x-auto">
                <For each={items()}>
                  {(item, index) => (
                    <button
                      type="button"
                      class={`max-w-40 truncate rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        index() === state().itemIndex
                          ? "border-primary bg-primary/15 font-semibold"
                          : "border-border text-muted-foreground hover:bg-accent/10"
                      }`}
                      data-testid="projection-jump"
                      data-jump-index={index()}
                      onClick={() => setPresenter(goToSlide(index(), 0, counts()))}
                    >
                      {item.title}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </div>
        )}
      </Show>

      {/* ── Diálogo: alta manual ───────────────────────────────────────────── */}
      <FormDialog
        open={addOpen()}
        onOpenChange={setAddOpen}
        title={t(msg`Add projectable item`)}
        icon={<Plus size={20} />}
        submitTestId="projection-add-submit"
        disabled={!addTitle().trim() || !addTarget()}
        onSubmit={addItem}
      >
        <Field id="prj-kind" label={t(msg`Type`)}>
          <Select
            options={PROJECTABLE_KINDS.map((k) => ({
              value: k,
              label: kindLabel(k),
            }))}
            value={addKind()}
            onChange={setAddKind}
            aria-label={t(msg`Type`)}
          />
        </Field>
        <Field id="prj-target" label={t(msg`Project at`)}>
          <Select
            options={writeTargets()}
            value={addTarget()}
            onChange={setAddTarget}
            aria-label={t(msg`Project at`)}
          />
        </Field>
        <Field
          id="prj-title"
          label={addKind() === "verse" ? t(msg`Reference (e.g. John 3:16)`) : t(msg`Title`)}
          value={addTitle()}
          onInput={setAddTitle}
          testId="projection-title-input"
        />
        <Field
          id="prj-content"
          label={
            addKind() === "verse"
              ? t(msg`Verse text (paste it manually)`)
              : t(msg`Content (blank line = new slide)`)
          }
        >
          <TextArea
            id="prj-content"
            rows={5}
            value={addContent()}
            onInput={setAddContent}
            class="font-sans"
            testId="projection-content-input"
          />
        </Field>
        <Field id="prj-notes" label={t(msg`Presenter notes (never projected)`)}>
          <TextArea
            id="prj-notes"
            rows={2}
            value={addNotes()}
            onInput={setAddNotes}
            class="font-sans"
            testId="projection-notes-input"
          />
        </Field>
      </FormDialog>

      {/* ── Diálogo: importar setlist ──────────────────────────────────────── */}
      <FormDialog
        open={importOpen()}
        onOpenChange={setImportOpen}
        title={t(msg`Import setlist into projection`)}
        icon={<ListPlus size={20} />}
        submitTestId="projection-import-submit"
        submitLabel={t(msg`Import`)}
        disabled={!importSetlist() || !importTarget()}
        onSubmit={runImport}
      >
        <p class="text-xs text-muted-foreground">
          {t(
            msg`Copies the setlist songs (lyrics as slides, key and tempo as presenter notes) into the projection scope, so the projector device never needs the music library.`,
          )}
        </p>
        <Field id="imp-setlist" label={t(msg`Setlist`)}>
          <Select
            options={(setlists.data ?? []).map((s) => ({
              value: s.id,
              label: s.title ?? s.id,
            }))}
            value={importSetlist()}
            onChange={setImportSetlist}
            aria-label={t(msg`Setlist`)}
            emptyLabel={t(msg`No setlists synced`)}
          />
        </Field>
        <Field id="imp-target" label={t(msg`Project at`)}>
          <Select
            options={writeTargets()}
            value={importTarget()}
            onChange={setImportTarget}
            aria-label={t(msg`Project at`)}
          />
        </Field>
      </FormDialog>
    </div>
  );
}
